import {getCopilotSpacePath, SLUG_ERROR_MESSAGE} from '@github-ui/copilot-chat/utils/custom-copilots-helpers'
import {useAutoSpaceName} from '../hooks'
import type {CopilotSpacesConfigPayload} from '../types'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useNavigate} from '@github-ui/use-navigate'
import {Dialog, FormControl, Stack, Textarea, TextInput} from '@primer/react'
import {useCallback, useEffect, useRef, useState} from 'react'

import {useUpsertCopilotSpace} from '../hooks/use-upsert-copilot-space'
import {type OwnerItem, SpacesOwnerDropdown} from './SpacesOwnerDropdown'
import styles from './CreateSpaceDialog.module.css'
import {SpaceDialogFooter} from './SpaceDialogFooter'

export function CreateSpaceDialog({closeDialog}: {closeDialog: () => void}) {
  const navigate = useNavigate()

  const nameInputRef = useRef<HTMLInputElement>(null)
  const {name, setName, defaultName} = useAutoSpaceName()
  const [description, setDescription] = useState('')
  const [owner, setOwner] = useState<OwnerItem | null>(null)
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({})
  const {upsertCopilotSpace, isPending: isSaving} = useUpsertCopilotSpace()

  const {
    copilotSpacesConfig: {maxDescriptionLength},
  } = useAppPayload<CopilotSpacesConfigPayload>()

  const clearErrors = useCallback(
    (fields: string[]) => {
      const updatedErrorMessages = {...errorMessages}
      for (const field of fields) {
        if (updatedErrorMessages[field]) {
          delete updatedErrorMessages[field]
        }
      }
      setErrorMessages(updatedErrorMessages)
    },
    [errorMessages],
  )
  const dropdownOnSelect = useCallback((newOwner: OwnerItem) => setOwner(newOwner), [])

  useEffect(() => {
    if (nameInputRef.current && name === defaultName) {
      nameInputRef.current.select()
    }
  }, [name, defaultName])

  const saveSpace = async (): Promise<void> => {
    if (isSaving) return

    try {
      const newSpace = await upsertCopilotSpace({
        name,
        description,
        ownerId: owner?.id,
        ownerType: owner?.type,
      })

      setErrorMessages({})
      // navigate to the newly created space
      const path = getCopilotSpacePath(newSpace)
      return navigate(path)
    } catch (errors) {
      const errs = errors ?? {}
      setErrorMessages(errs as Record<string, string>)
    }
  }
  const handleCancel = () => {
    closeDialog()
  }

  return (
    <Dialog
      position={{narrow: 'fullscreen'}}
      title={'New Space'}
      onClose={closeDialog}
      width="large"
      aria-label="New Space"
      className={styles.createSpaceDialog}
      renderFooter={() => (
        <SpaceDialogFooter onCancel={handleCancel} onSave={saveSpace} isLoading={isSaving} disabled={!owner} />
      )}
    >
      <form
        onSubmit={async e => {
          e.preventDefault()
          e.stopPropagation()
          await saveSpace()
        }}
      >
        <Stack>
          <FormControl>
            <FormControl.Label required>Name</FormControl.Label>
            <TextInput
              ref={nameInputRef}
              autoFocus
              block
              data-testid="copilot-space-name-input"
              name="copilot-space-name"
              value={name}
              onChange={e => {
                setName(e.target.value)
                clearErrors(['name', 'slug'])
              }}
            />
            {errorMessages.name ? (
              <FormControl.Validation variant="error">
                <span>Name {errorMessages.name}</span>
              </FormControl.Validation>
            ) : errorMessages.slug ? (
              <FormControl.Validation variant="error">
                <span>{SLUG_ERROR_MESSAGE}</span>
              </FormControl.Validation>
            ) : null}
          </FormControl>
          <SpacesOwnerDropdown onSelect={dropdownOnSelect} required showCaption={false} />
          <FormControl>
            <FormControl.Label required={false}>Description</FormControl.Label>
            <Textarea
              block
              name="description"
              value={description}
              rows={3}
              resize="vertical"
              className={styles.textAreaWrapper}
              onChange={e => {
                setDescription(e.target.value)
                clearErrors(['description'])
              }}
            />
            {errorMessages.description ? (
              <FormControl.Validation variant="error">
                <span>Description {errorMessages.description}</span>
              </FormControl.Validation>
            ) : (
              <CharacterCount text={description} promptCharLimit={maxDescriptionLength} />
            )}
          </FormControl>
        </Stack>
      </form>
    </Dialog>
  )
}

function CharacterCount({text, promptCharLimit}: {text: string; promptCharLimit: number}) {
  const tooLong = text.length > promptCharLimit
  const currentCount = (
    <>
      {text.length.toLocaleString()} / {promptCharLimit.toLocaleString()} characters
    </>
  )
  return (
    <>
      {tooLong ? (
        <FormControl.Validation variant="error">{currentCount}</FormControl.Validation>
      ) : (
        // This is a direct child, it just doesn't know that because it is a fragment within a function call
        // eslint-disable-next-line primer-react/direct-slot-children
        <FormControl.Caption>{currentCount}</FormControl.Caption>
      )}
    </>
  )
}

try{ CreateSpaceDialog.displayName ||= 'CreateSpaceDialog' } catch {}
try{ CharacterCount.displayName ||= 'CharacterCount' } catch {}